// Wallet PCF del vendedor: CLABE o teléfono, qué dice la verificación y cómo se ve la vinculada.
import type { RetailOrg } from '@fiado/type-kit';
import { formatDateMx } from '@/lib/dateHelpers';
import type { RetailUser } from '../api/useRetailOrg';

/** El permiso con que `retail-org` vincula y desvincula la wallet de un usuario. */
export const WALLET_LINK_PERMISSION = 'retail.user.update';

const CLABE_LENGTH = 18;

export type WalletMethod = `${RetailOrg.RetailUserWalletLinkMethodEnum}`;

/** Lo que se teclea en el campo de la CLABE: solo dígitos, hasta 18. */
export function clabeDigits(text: string): string {
    return text.replace(/\D/g, '').slice(0, CLABE_LENGTH);
}

export function isClabe(text: string): boolean {
    return /^\d{18}$/.test(text);
}

/** Los dígitos del número sin la lada de país (`+52` o el viejo `+52 1` de celular). */
function withoutCountryCode(text: string): string {
    const digits = text.replace(/\D/g, '');
    if (digits.length === 13 && digits.startsWith('521')) return digits.slice(3);
    return digits.length === 12 && digits.startsWith('52') ? digits.slice(2) : digits;
}

/** Los 10 dígitos de un teléfono de México, con o sin `+52`; cualquier otra cosa, vacío. */
export function localMxPhone(text: string): string {
    const local = withoutCountryCode(text);
    return /^\d{10}$/.test(local) ? local : '';
}

/** Lo que se teclea o se pega en el campo del teléfono: el número sin `+52`, hasta 10 dígitos. */
export function phoneDigits(text: string): string {
    return withoutCountryCode(text).slice(0, 10);
}

export function isMxPhone(digits: string): boolean {
    return /^\d{10}$/.test(digits);
}

export interface LookupOutcome {
    found: boolean;
    linkable: boolean;
    tone: 'success' | 'warning' | 'danger';
    message: string;
}

export function lookupOutcome(
    result: RetailOrg.LookupRetailUserWalletResponse,
    searchedBy: WalletMethod,
): LookupOutcome {
    if (!result.found) {
        return {
            found: false,
            linkable: false,
            tone: 'danger',
            message:
                searchedBy === 'PHONE'
                    ? 'Ese teléfono no tiene cuenta en Fiado. Sin wallet, sus comisiones quedan pendientes.'
                    : 'No encontramos una wallet PCF con esa CLABE.',
        };
    }
    if (!result.active) {
        return {
            found: true,
            linkable: false,
            tone: 'warning',
            message: 'La cuenta existe, pero no está activa: no se puede vincular.',
        };
    }
    return {
        found: true,
        linkable: true,
        tone: 'success',
        message: `Titular: ${result.holderName ?? 'sin nombre registrado'} · cuenta activa`,
    };
}

/** Lo que sabe el formulario: nada capturado, capturada sin verificar o lista para vincular. */
export type WalletFieldState =
    | { kind: 'empty' }
    | { kind: 'unverified' }
    | { kind: 'verified'; body: RetailOrg.LinkRetailUserWalletRequest };

type WalletFields = Pick<RetailUser, 'fiadoDirectoryId' | 'walletClabeLast4' | 'walletLinkedAt'>;

/** "CLABE ···0004 · desde 12/oct/2026", o `null` si no tiene wallet. */
export function linkedWalletText(user: WalletFields): string | null {
    if (!user.fiadoDirectoryId) return null;
    const how = user.walletClabeLast4 ? `CLABE ···${user.walletClabeLast4}` : 'Por teléfono';
    return user.walletLinkedAt ? `${how} · desde ${formatDateMx(user.walletLinkedAt)}` : how;
}

/** La etiqueta corta de la lista de usuarios. */
export function walletBadgeText(user: WalletFields): string {
    if (!user.fiadoDirectoryId) return 'Sin wallet';
    return user.walletClabeLast4 ? `Wallet ···${user.walletClabeLast4}` : 'Wallet por teléfono';
}
